import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UploadZone } from "@/components/admin/UploadZone";
import { VideoRow } from "@/components/admin/VideoRow";
import { FolderManager } from "@/components/admin/FolderManager";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portraitUrl, setPortraitUrl] = useState(null);
  const [portraitBusy, setPortraitBusy] = useState(false);
  const [folders, setFolders] = useState([]);
  const [catFilter, setCatFilter] = useState("All");
  const portraitInput = useRef(null);
  const dragIndex = useRef(null);

  const usedCats = [...new Set(videos.flatMap((v) => (v.categories?.length ? v.categories : [v.category]).filter(Boolean)))];
  const visibleVideos = catFilter === "All" ? videos : videos.filter((v) => (v.categories?.length ? v.categories : [v.category]).includes(catFilter));

  const load = async () => {
    try {
      const [v, e, s, fo] = await Promise.all([api.get("/admin/videos"), api.get("/admin/enquiries"), api.get("/settings"), api.get("/folders")]);
      setVideos(v.data);
      setEnquiries(e.data);
      setFolders(fo.data);
      setPortraitUrl(s.data.portrait_url ? `${process.env.REACT_APP_BACKEND_URL}${s.data.portrait_url}?t=${Date.now()}` : null);
    } catch {
      toast.error("Could not load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  const uploadPortrait = async (f) => {
    if (!f) return;
    setPortraitBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.put("/admin/settings/portrait", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPortraitUrl(`${process.env.REACT_APP_BACKEND_URL}/api/settings/portrait?t=${Date.now()}`);
      toast.success("Portrait updated on the live site.");
    } catch {
      toast.error("Portrait upload failed.");
    } finally {
      setPortraitBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onChanged = (updated) => setVideos((vs) => vs.map((v) => (v.id === updated.id ? updated : v)));
  const onDeleted = (id) => setVideos((vs) => vs.filter((v) => v.id !== id));
  const onUploaded = (v) => setVideos((vs) => [...vs, v]);

  const handleDragStart = (e, index) => { dragIndex.current = index; e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleDrop = async (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null) return;
    const rows = e.currentTarget.parentElement.children;
    const to = [...rows].indexOf(e.currentTarget);
    if (to < 0 || to === from) return;
    const reordered = [...videos];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setVideos(reordered);
    dragIndex.current = null;
    try {
      await api.put("/admin/videos-reorder", { ids: reordered.map((v) => v.id) });
      toast.success("Order updated on the live site.");
    } catch {
      toast.error("Could not save order.");
      load();
    }
  };

  const signOut = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-cream-dark bg-cream sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-serif text-xl text-ink">{SITE.name} — <span className="text-ink-soft">Studio</span></p>
          <div className="flex items-center gap-5">
            <a href="/" data-testid="admin-view-site" className="text-xs uppercase tracking-[0.16em] font-semibold text-ink-soft hover:text-ink transition-colors">View site</a>
            <span className="hidden sm:inline text-xs text-ink-soft" data-testid="admin-email-label">{admin?.email}</span>
            <button onClick={signOut} data-testid="admin-logout-button"
              className="text-xs uppercase tracking-[0.16em] font-semibold border border-ink px-4 py-2 text-ink hover:bg-ink hover:text-cream transition-colors">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="videos">
          <TabsList className="bg-cream-dark rounded-none" data-testid="admin-tabs">
            <TabsTrigger value="videos" data-testid="tab-videos" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-cream">Video Wall ({videos.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-cream">Enquiries ({enquiries.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="videos" className="mt-8 space-y-8">
            <div className="border border-cream-dark bg-white p-6 flex items-center gap-5" data-testid="portrait-settings-card">
              <div className="w-16 aspect-[4/5] bg-cream-dark overflow-hidden shrink-0">
                {portraitUrl && <img src={portraitUrl} alt="Site portrait" className="w-full h-full object-cover" data-testid="portrait-preview" />}
              </div>
              <div className="flex-1">
                <p className="eyebrow text-ink-soft">About-section portrait</p>
                <p className="text-xs text-ink-soft mt-1">Shown in "About Me" on the live site. Displayed in black & white.</p>
              </div>
              <input ref={portraitInput} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="portrait-upload-input" onChange={(e) => uploadPortrait(e.target.files?.[0])} />
              <button onClick={() => portraitInput.current?.click()} disabled={portraitBusy} data-testid="portrait-upload-button"
                className="text-xs uppercase tracking-[0.14em] font-semibold border border-ink px-5 py-2.5 text-ink hover:bg-ink hover:text-cream transition-colors disabled:opacity-50">
                {portraitBusy ? "Uploading…" : portraitUrl ? "Replace photo" : "Upload photo"}
              </button>
            </div>
            <UploadZone onUploaded={onUploaded} folders={folders} />
            <FolderManager folders={folders} onChange={setFolders}
              videoCounts={videos.reduce((acc, v) => { if (v.folder_id) acc[v.folder_id] = (acc[v.folder_id] || 0) + 1; return acc; }, {})} />
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <p className="eyebrow text-ink-soft">Live order — drag to rearrange</p>
                <p className="text-xs text-ink-soft">{videos.filter((v) => v.visible).length} live · {videos.filter((v) => !v.visible).length} hidden</p>
              </div>
              {usedCats.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4" data-testid="admin-category-filters">
                  {["All", ...usedCats].map((c) => (
                    <button key={c} data-testid={`admin-filter-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setCatFilter(c)}
                      className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                        catFilter === c ? "bg-ink text-cream border-ink" : "border-cream-deeper text-ink-soft hover:border-ink"
                      }`}>
                      {c}
                    </button>
                  ))}
                  {catFilter !== "All" && <span className="text-[11px] text-ink-soft self-center ml-2">Reordering paused while filtered</span>}
                </div>
              )}
              {loading ? (
                <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-cream-dark animate-pulse" />)}</div>
              ) : videos.length === 0 ? (
                <p className="text-ink-soft py-10" data-testid="admin-no-videos">No videos yet — upload your first one above.</p>
              ) : (
                <div className="space-y-2" data-testid="admin-video-list">
                  {visibleVideos.map((v, i) => (
                    <VideoRow key={v.id} video={v} index={i} folders={folders} canDrag={catFilter === "All"}
                      onChanged={onChanged} onDeleted={onDeleted}
                      onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="enquiries" className="mt-8">
            {enquiries.length === 0 ? (
              <p className="text-ink-soft py-10" data-testid="admin-no-enquiries">No enquiries yet. Submissions from the contact form appear here.</p>
            ) : (
              <div className="space-y-3" data-testid="admin-enquiry-list">
                {enquiries.map((q) => (
                  <div key={q.id} className="bg-white border border-cream-dark p-5" data-testid={`enquiry-${q.id}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-semibold text-ink">{q.name} {q.brand ? <span className="text-ink-soft font-normal">— {q.brand}</span> : null}</p>
                      <p className="text-xs text-ink-soft">{new Date(q.created_at).toLocaleDateString()}</p>
                    </div>
                    <p className="text-sm text-ink mt-1">{q.email}{q.website ? ` · ${q.website}` : ""}{q.service ? ` · ${q.service}` : ""}{q.budget ? ` · ${q.budget}` : ""}</p>
                    <p className="text-sm text-ink-soft mt-2 leading-relaxed">{q.details}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
